import CallService, { Conversation, ITakeOver, NoTakeOver, TakeOverTypeEnum } from '@outbound-ai/softphone'
import { store } from 'appState/store'
import { env } from 'configs/env'
import { authApi } from 'features/auth/authApi'
import {
  CallMessageDirectionEnum,
  CallParticipantTypeEnum,
  CallTypeToJobTypeEnum,
  ICall,
  ITranscriptMessage,
} from 'features/calls/types'
import { sendLogs } from 'utils'
import { dateDiffToHHMMSS } from 'utils/date'
import { notificator } from 'utils/notificator/notificator'
import { v4 as uuid } from 'uuid'
import { IUserPhone } from '../../settings/settingsTypes'
import { updateHumanAgentCallInProgress, updateLiveCall } from '../callsSlice'

class Softphone {
  private _callService: CallService | null = null
  private _call: ICall | null = null
  private _conversation: Conversation | null = null
  private _transcript: ITranscriptMessage[] = []
  private _takeOver: ITakeOver = NoTakeOver
  private _audioCtx: AudioContext | null = null
  private _availableTakeOverType: TakeOverTypeEnum = TakeOverTypeEnum.none
  private _userTakeOverPhone: IUserPhone | null = null

  constructor() {
    this._initCallsService()
  }

  public async initConnection(call: ICall, transcript: ITranscriptMessage[] = []) {
    if (!this._callService) this._initCallsService()
    if (!this._call || this._call.jobId !== call.jobId) {
      this.disconnect()
      this._call = call
      this._conversation = await this._callService!.getConversationAsync(
        this._call.jobId,
        (await authApi.getAuthToken()) || '',
        //* Left until we have a decision from product if the user should be able to choose a different mic for the calls
        //storage.get(StorageEnum.microphoneDevideId),
      )
      this._transcript = transcript
      this._audioCtx = this._conversation.audioCtx || null
      this._initSocketListeners(call)

      // relies on calls service take-over participants
      this._updateTakeOverMic(true)

      const startTime = call.jobStarted || call.jobQueued || call.jobRequested
      store.dispatch(
        updateLiveCall({
          startTime,
          isConnected: true,
        }),
      )
    }
  }

  public get audioCtx() {
    return this._audioCtx
  }

  public resumeAudioContext = async () => {
    if (!this._conversation) return
    if (this._audioCtx?.state === 'suspended') {
      await this._audioCtx.resume()
      // Fixes bug where microphone cannot be resumed in case it is created on a suspended audio context
      await this._conversation.audio.createAudioWorklet()
      //* Left here until we have a decsion on implementing the functionality to choose the default microphone for calls
      //await this._conversation.audio.createAudioWorklet(storage.get(StorageEnum.microphoneDevideId))
    }
  }

  public disconnect = () => {
    this._resetState()
    store.dispatch(updateHumanAgentCallInProgress({ callInProgress: false }))
    store.dispatch(
      updateLiveCall({
        isConnected: false,
      }),
    )
  }

  public updateCallTranscript = (newTranscript: ITranscriptMessage[]) => {
    this._transcript = newTranscript
    store.dispatch(
      updateLiveCall({
        transcript: newTranscript,
      }),
    )
  }

  public updateUserTakeOverPhone = (userTakeOverPhone: IUserPhone | null) => {
    this._userTakeOverPhone = userTakeOverPhone
    store.dispatch(updateLiveCall({ userTakeOverPhone }))
  }

  public updateAvailableTakeOverType = (availableTakeOverType: TakeOverTypeEnum) => {
    this._availableTakeOverType = availableTakeOverType
    store.dispatch(updateLiveCall({ availableTakeOverType }))
  }

  public takeOver = async () => {
    if (TakeOverTypeEnum.none === this._availableTakeOverType) {
      notificator.notify({
        heading:
          'This claim does not have any take over options available. You did not select any option in the settings -> calls menu.',
        type: 'error',
      })
      sendLogs({
        message:
          'This claim does not have any take over options available. You did not select any option in the settings -> calls menu.',
      })
    }

    if (this._conversation) {
      this._muteUnmuteForTakeover(this._availableTakeOverType, true)
      if (this._userTakeOverPhone) {
        this._conversation.agentTakeOver({ type: TakeOverTypeEnum.dialed, phoneNumber: this._userTakeOverPhone.value })
      } else {
        if (this._call?.jobType === CallTypeToJobTypeEnum.standard) {
          setTimeout(() => {
            if (this._conversation?.isAudioExist) {
              this._conversation.agentTakeOver()
            }
          }, 3000)
        } else {
          this._conversation.agentTakeOver()
        }
      }
    }
  }

  public toggleMute = () => {
    if (!this._conversation) return
    this._conversation.inputMuted ? this._conversation.unmuteInput() : this._conversation.muteInput()
    store.dispatch(
      updateLiveCall({
        isMuted: this._conversation.inputMuted,
      }),
    )
  }

  public sendDTMFCode = (code: string) => {
    this._conversation?.synthesizeTouchTones(code)
  }

  public sendAudioMessage = (message: string) => {
    this._conversation?.synthesizeSpeech(message)
  }

  public hangUp = () => {
    this._conversation?.hangup()
    this.disconnect()
    store.dispatch(
      updateLiveCall({
        isConnected: false,
      }),
    )
  }

  public muteAudio = () => {
    if (!this._conversation) return
    this._conversation.muteOutput()
    store.dispatch(
      updateLiveCall({
        isMutedAudio: true,
      }),
    )
  }

  public unmuteAudio = () => {
    if (!this._conversation) return
    this._conversation.unmuteOutput()
    store.dispatch(
      updateLiveCall({
        isMutedAudio: false,
      }),
    )
  }

  public muteMicrophone = () => {
    if (!this._conversation) return
    this._conversation.muteInput()
    store.dispatch(
      updateLiveCall({
        isMuted: true,
      }),
    )
  }

  public unmuteMicrophone = () => {
    if (!this._conversation) return
    this._conversation.unmuteInput()
    store.dispatch(
      updateLiveCall({
        isMuted: false,
      }),
    )
  }

  public isAudioExist = () => {
    return this._conversation ? this._conversation.isAudioExist() : false
  }

  private _initSocketListeners(call: ICall) {
    if (this._conversation) {
      this._call = call
      this._conversation.onTranscriptAvailable = (participantId: string, participantType: string, message: string) => {
        this._transcript = [
          ...this._transcript,
          {
            id: uuid(),
            participantId,
            participantType: participantType as CallParticipantTypeEnum,
            direction: CallMessageDirectionEnum.incoming,
            message,
            created: new Date().toISOString(),
          },
        ]
        store.dispatch(updateLiveCall({ transcript: this._transcript }))
      }

      this._conversation.onHoldForHumanEvent = async (message: string) => {
        if (
          'stop' === message &&
          // we did not show the take over model and sent the desktop notification
          !store.getState().calls.talkToPayorRepLiveCall.isActivePayorAgentReady &&
          // call is not taken over already
          TakeOverTypeEnum.none === this._takeOver.type
        ) {
          if (TakeOverTypeEnum.dialed === this._availableTakeOverType) {
            return await this.takeOver()
          }
          store.dispatch(updateLiveCall({ isActivePayorAgentReady: true }))
        }
      }

      // TakeOver state may change at any time - particularly when we rejoin a taken-over call.
      this._conversation.onTakeOverStateChanged = async (takeOver: ITakeOver) => {
        const takeOverChange = this._takeOver.type !== takeOver.type
        if (takeOverChange && this._call) {
          this._takeOver = takeOver
          store.dispatch(updateLiveCall({ takeOver: takeOver }))
          this._updateTakeOverMic(true)
          if (
            [CallTypeToJobTypeEnum.standard, CallTypeToJobTypeEnum.diagnosticHeadset].includes(this._call.jobType) &&
            this._takeOver.type === TakeOverTypeEnum.none
          ) {
            return await this.takeOver()
          }
        }
      }

      // The call can be disconnected at any time.
      this._conversation.onConnectionStateChanged = (connected) => {
        sendLogs({ message: 'onConnectionStateChanged >> connection state changed' + connected })
        if (!connected) {
          this._resetState()
        } else {
          if (
            call.jobType === CallTypeToJobTypeEnum.diagnosticHeadset ||
            call.jobType === CallTypeToJobTypeEnum.standard
          ) {
            this.takeOver()
          }
        }
      }
    }

    const checkConnectionInterval = setInterval(() => {
      store.dispatch(
        updateLiveCall({
          elapsedTime:
            dateDiffToHHMMSS(new Date().toISOString(), store.getState().calls.talkToPayorRepLiveCall.startTime, true) ||
            '0:00:00',
        }),
      )

      if (!this._conversation?.connected) {
        store.dispatch(updateLiveCall({ isConnected: false }))
        clearInterval(checkConnectionInterval)
      }
    }, 1000)
  }

  private _initCallsService() {
    // if (!env.VITE_CALLS_SERVICE_WSS) throw new Error('Missing calls service wss env variable')
    if (!this._callService) this._callService = new CallService(env.VITE_CALLS_SERVICE_WSS!)
    this._callService.onLog = (msg) => {
      sendLogs({ message: 'Softphone onLog >> ' + msg })
    }
  }

  private _resetState() {
    this._conversation?.disconnect()
    this._takeOver = NoTakeOver
    this._transcript = []
    this._conversation = null
    this._call = null
  }

  private _muteUnmuteForTakeover(takeOverType: TakeOverTypeEnum, forceMicState = false) {
    if (!this._conversation) return
    if (TakeOverTypeEnum.dialed === takeOverType) {
      this._conversation.muteInput()
      if (!this._conversation.outputMuted) this._conversation.muteOutput()
    } else if (forceMicState && TakeOverTypeEnum.browser === takeOverType) {
      this._conversation.unmuteInput()
      if (this._conversation.outputMuted) this._conversation.unmuteOutput()
    }
    store.dispatch(
      updateLiveCall({
        isMuted: this._conversation.inputMuted,
        isMutedAudio: this._conversation.outputMuted,
      }),
    )
  }

  private _updateTakeOverMic(forceMicState = false) {
    if (!this._conversation) return
    this._muteUnmuteForTakeover(this._takeOver.type, forceMicState)
  }
}

export const softphone = new Softphone()
